<template>
  <transition name="email-indicator-fade">
    <div v-if="visible" class="email-sending-indicator" :class="statusClass">
      <div class="email-sending-indicator__content">
        <Spinner v-if="status === 'uploading'" size="small" />
        <fluent-icon
          v-else-if="status === 'sent'"
          icon="checkmark-circle"
          size="16"
          class="email-sending-indicator__icon--success"
        />
        <fluent-icon
          v-else-if="status === 'error'"
          icon="dismiss-circle"
          size="16"
          class="email-sending-indicator__icon--error"
        />
        <span class="email-sending-indicator__text">{{ statusText }}</span>
      </div>
      <button
        v-if="status === 'error'"
        class="email-sending-indicator__dismiss"
        @click="dismiss"
      >
        {{ $t('CONVERSATION.EMAIL_SEND_DISMISS') }}
      </button>
    </div>
  </transition>
</template>

<script>
import { emitter } from 'shared/helpers/mitt';
import { EMAIL_SEND_STATUS_EVENT } from 'dashboard/helper/emailBackgroundSend';
import Spinner from 'shared/components/Spinner.vue';
import timerLifecycleMixin from 'dashboard/mixins/timerLifecycleMixin';

const AUTO_DISMISS_MS = 4000;

export default {
  name: 'EmailSendingIndicator',
  components: { Spinner },
  mixins: [timerLifecycleMixin],

  data() {
    return {
      visible: false,
      status: null, // 'uploading' | 'sent' | 'error'
      partialFailures: 0,
      errorType: null,
      dismissTimer: null,
    };
  },

  computed: {
    statusClass() {
      return {
        'email-sending-indicator--uploading': this.status === 'uploading',
        'email-sending-indicator--sent': this.status === 'sent',
        'email-sending-indicator--error': this.status === 'error',
      };
    },
    statusText() {
      if (this.status === 'uploading') {
        return this.$t('CONVERSATION.EMAIL_SEND_UPLOADING');
      }
      if (this.status === 'sent') {
        if (this.partialFailures > 0) {
          return this.$t('CONVERSATION.EMAIL_SEND_SENT_WITH_WARNINGS', {
            count: this.partialFailures,
          });
        }
        return this.$t('CONVERSATION.EMAIL_SEND_SENT');
      }
      if (this.status === 'error') {
        if (this.errorType === 'total_failure') {
          return this.$t('CONVERSATION.EMAIL_SEND_TOTAL_FAILURE');
        }
        if (this.errorType === 'unconfirmed') {
          return this.$t('CONVERSATION.EMAIL_SEND_UNCONFIRMED');
        }
        return this.$t('CONVERSATION.EMAIL_SEND_UPLOAD_FAILED');
      }
      return '';
    },
  },

  mounted() {
    emitter.on(EMAIL_SEND_STATUS_EVENT, this.onStatusChange);
  },

  beforeUnmount() {
    emitter.off(EMAIL_SEND_STATUS_EVENT, this.onStatusChange);
    this.clearDismissTimer();
  },

  methods: {
    onStatusChange({ status, partialFailures = 0, errorType = null }) {
      this.clearDismissTimer();
      this.status = status;
      this.partialFailures = partialFailures;
      this.errorType = errorType;
      this.visible = true;

      if (status === 'sent') {
        this.dismissTimer = this.scheduleTimer(
          () => this.dismiss(),
          AUTO_DISMISS_MS
        );
      }
    },
    dismiss() {
      this.visible = false;
      this.clearDismissTimer();
    },
    clearDismissTimer() {
      if (this.dismissTimer) {
        this.clearScheduledTimer(this.dismissTimer);
        this.dismissTimer = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.email-sending-indicator {
  position: fixed;
  bottom: 16px;
  left: 16px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  line-height: 1.4;
  transition: all 0.3s ease;

  &--uploading {
    background: var(--white);
    border: 1px solid var(--s-100);
    color: var(--s-700);
  }

  &--sent {
    background: var(--g-50, #f0fdf4);
    border: 1px solid var(--g-200, #bbf7d0);
    color: var(--g-700, #15803d);
  }

  &--error {
    background: var(--r-50, #fef2f2);
    border: 1px solid var(--r-200, #fecaca);
    color: var(--r-700, #b91c1c);
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon--success {
    color: var(--g-500, #22c55e);
  }

  &__icon--error {
    color: var(--r-500, #ef4444);
  }

  &__text {
    max-width: min(520px, calc(100vw - 140px));
    white-space: normal;
  }

  &__dismiss {
    background: none;
    border: 1px solid currentColor;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    cursor: pointer;
    color: inherit;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
}

.email-indicator-fade-enter-active,
.email-indicator-fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.email-indicator-fade-enter,
.email-indicator-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
